import {
	Children,
	cloneElement,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useState,
} from "react";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useSelector, useDispatch } from "react-redux";
import { setPartnerData as setPartnerDataAction } from "app/reducers/partnerDataActionCreators";
import {
	logout as logoutAction,
	setTokenFromPartner,
	thunkSetId as thunkSetIdAction,
	updateAuthToken,
} from "app/pages/Auth/AuthActions";
import { setSponsorId as setSponsorIdAction } from "app/pages/Account/Parrainage/sponsorActionCreators";
import { updateMarketingData as updateMarketingDataAction } from "app/reducers/marketingActionCreators";
import { useLocation, useSearchParams } from "react-router-dom";
import { parse, stringify } from "qs";
import {
	buildPartnerTokenFromQuery,
	extractPartnerDataFromQuery,
	extractPartnerTokensFromQuery,
} from "app/utils/urlDataExtractor";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import { updateStrategy as updateStrategyAction } from "app/reducers/partnerActionCreators";
import { updateEmail as updateEmailAction } from "app/reducers/emailActionCreators";
import { isServerSide } from "app/utils/utils";
import { isAuthenticated as isAuthenticatedSelector } from "app/pages/Auth/authSelectors";
import appGlobalsContext from "app/AppGlobalsContext";
import { setPfsParams as setPfsParamsAction } from "app/features/pfsParams/pfsParamsActionCreators";

const useIsomorphicLayoutEffect = isServerSide ? useEffect : useLayoutEffect;

const QueriesParser = ({ children }) => {
	const dispatch = useDispatch();
	const { track } = useAnalytics();
	const email = useSelector(state => state.email);
	const partnerDataNames = useSelector(state => state.brand.partnerDataNames) || [];
	const partnerTokensNames = useSelector(state => state.brand.partnerTokensNames) || [];
	const isAuthenticated = useSelector(state => isAuthenticatedSelector(state));
	const setPartnerData = useCallback(arg => dispatch(setPartnerDataAction(arg)), [dispatch]);
	const thunkSetId = useCallback(
		arg => dispatch(thunkSetIdAction(arg, track)),
		[dispatch, track]
	);
	const setSponsorId = useCallback(arg => dispatch(setSponsorIdAction(arg)), [dispatch]);
	const logout = useCallback((...args) => dispatch(logoutAction(...args)), [dispatch]);
	const updateMarketingData = useCallback(
		arg => dispatch(updateMarketingDataAction(arg)),
		[dispatch]
	);
	const updateStrategy = useCallback(arg => dispatch(updateStrategyAction(arg)), [dispatch]);
	const updateEmail = useCallback(arg => dispatch(updateEmailAction(arg)), [dispatch]);
	const setPfsParams = useCallback(arg => dispatch(setPfsParamsAction(arg)), [dispatch]);
	const [, setSearchParams] = useSearchParams();
	const { shop } = useContext(appGlobalsContext);
	const { search } = useLocation();
	const query = parse(search, { ignoreQueryPrefix: true });
	const { uuid, email: emailQuery, strategy, sponsorId, authToken, ...queriesToKeep } = query;

	const {
		source,
		utm_medium,
		utm_source,
		utm_campaign,
		utm_content,
		utm_term,
		utx_medium,
		utx_source,
		utx_campaign,
		utx_content,
		utx_term,
	} = queriesToKeep;

	// Extraction des paramètres pfs-
	const extractedPfsParams = {};
	Object.keys(query).forEach(key => {
		if (key.startsWith("pfs-")) {
			extractedPfsParams[key] = query[key];
		}
	});

	const partnerData = extractPartnerDataFromQuery(partnerDataNames, query);
	if (!isEmpty(partnerData)) {
		setPartnerData(partnerData);
	}

	const partnerTokenNames = partnerTokensNames || [];

	const partnerTokensInQuery = extractPartnerTokensFromQuery({
		partnerTokenNames,
		query,
	});

	const hasIdentityQuery = Boolean(
		uuid || emailQuery || strategy || sponsorId || authToken || !isEmpty(partnerTokensInQuery)
	);

	// Les liens d'identité doivent être appliqués avant le montage des enfants : le serveur les
	// diffère lui aussi, sinon son HTML diffère du 1er rendu client et l'hydratation React 18 échoue.
	const [queryParsed, setQueryParsed] = useState(!hasIdentityQuery);

	const childrenWithProps = Children.map(children, child => {
		return cloneElement(child);
	});

	// Effet « layout » : les données marketing sont en store avant les useEffect des enfants.
	useIsomorphicLayoutEffect(() => {
		// Stocker les paramètres pfs- dans Redux
		if (!isEmpty(extractedPfsParams)) {
			setPfsParams(extractedPfsParams);
		}

		if (
			utm_medium ||
			utm_source ||
			utm_campaign ||
			utm_content ||
			source ||
			utm_term ||
			utx_medium ||
			utx_source ||
			utx_campaign ||
			utx_content ||
			utx_term
		) {
			const utmData = {};

			if (source) {
				utmData.source = source;
			}

			if (utm_medium) {
				utmData.utmMedium = utm_medium;
			}

			if (utx_medium) {
				utmData.utmMedium = utx_medium;
			}

			if (utm_source) {
				utmData.utmSource = utm_source;
			}

			if (utx_source) {
				utmData.utmSource = utx_source;
			}

			if (utm_campaign) {
				utmData.utmCampaign = utm_campaign;
			}

			if (utx_campaign) {
				utmData.utmCampaign = utx_campaign;
			}

			if (utm_content) {
				utmData.utmContent = utm_content;
			}

			if (utx_content) {
				utmData.utmContent = utx_content;
			}

			if (utm_term) {
				utmData.utmTerm = utm_term;
			}

			if (utx_term) {
				utmData.utmTerm = utx_term;
			}

			if (!isEmpty(utmData)) {
				updateMarketingData(utmData);
			}
		}
	}, []);

	// useEffect et non useLayoutEffect : react-router ignore un setSearchParams appelé avant ses effets.
	useEffect(() => {
		if (hasIdentityQuery) {
			if (!isEmpty(partnerTokensInQuery)) {
				// delete sensitive data from url to avoid user personal data to be shared
				partnerTokenNames.forEach(name => {
					delete queriesToKeep[name];
				});

				const computedPartnerToken = buildPartnerTokenFromQuery(partnerTokensInQuery);

				if (computedPartnerToken) {
					setTokenFromPartner(computedPartnerToken);
				}
			}

			if (authToken) {
				if (!isAuthenticated) {
					updateAuthToken({ authToken, shop });
				}
			}

			if (uuid) {
				thunkSetId(uuid);
			}

			if (sponsorId) {
				setSponsorId(sponsorId);
			}

			if (emailQuery) {
				// Si l'email dans l'url diffère de celui récupéré du localstorage
				if (email && email !== emailQuery) {
					// ... alors on déconnecte l'utilisateur pour éviter qu'une réservation se fasse avec un mauvais email
					logout({ track });
				} else {
					updateEmail(emailQuery);
				}
			}

			if (strategy) {
				updateStrategy(strategy);
			}

			const stringifyQuery = stringify({
				...queriesToKeep,
			});

			setSearchParams(stringifyQuery, { replace: true });
		}

		setQueryParsed(true);
	}, []);

	return <>{queryParsed && <>{childrenWithProps}</>}</>;
};

QueriesParser.propTypes = {
	email: PropTypes.string,
	partnerTokensNames: PropTypes.array,
	partnerDataNames: PropTypes.array,
	setPartnerData: PropTypes.func,
	thunkSetId: PropTypes.func,
	setSponsorId: PropTypes.func,
	isAuthenticated: PropTypes.bool,
	logout: PropTypes.func,
	updateMarketingData: PropTypes.func,
	updateStrategy: PropTypes.func,
	updateEmail: PropTypes.func,
	setPfsParams: PropTypes.func,
};

export default QueriesParser;
